/**
 * Kawaii Stop — Mini Cart Product Options
 * File: assets/ks-mini-cart.js
 * Version: 1.9.0
 *
 * Provides one branded cart-style modal for:
 * - recommendation "Choose options" / product preview
 * - active cart-item "Edit options"
 *
 * The shopper remains in the mini cart. No product-page redirect is used.
 *
 * V1.9 stability rebuild:
 * - owns all mini-cart quantity, remove, wishlist-remove, add and edit mutations
 * - blocks Umino's duplicate native cart requests for those controls
 * - resolves a fresh Shopify line-item key immediately before every change
 * - retries only stale-line errors; real inventory errors remain visible
 * - serializes mutations so Shield, discounts and drawer refreshes cannot race
 * - replaces variants with verified add-first/remove-second rollback safety
 * - preserves Kawaii Shield and refreshes only after the cart has settled
 */
(() => {
  "use strict";

  const RECOMMENDATION_TRIGGER = "[data-ks-mini-cart-quickview]";
  const EDIT_TRIGGER = "[data-ks-mini-cart-edit]";
  const MODAL_ID = "ks-mini-cart-product-modal";
  const EMBEDDED_PRODUCT_SELECTOR = "[data-ks-mini-cart-product-data]";
  const SECTION_ID = "form-mini-cart";
  const PROTECTION_TEXT =
    /(?:kawaii\s*shield|package\s*protection|shipping\s*(?:protection|insurance)|simply\s*(?:shipping|insurance))/i;
  const PROTECTION_NODE_SELECTOR = [
    '[class*="simply" i]',
    '[id*="simply" i]',
    '[class*="insurance" i]',
    '[id*="insurance" i]',
    '[class*="protection" i]',
    '[id*="protection" i]',
    '[class*="shield" i]',
    '[id*="shield" i]',
    '[data-variant-id]',
    '[data-product-variant-id]',
    'button[role="switch"]',
    'input[type="checkbox"]'
  ].join(",");
  const productCache = new Map();

  let protectionReconcileTimer = 0;
  let protectionMutationActive = false;
  let knownProtectionVariantId = 0;
  let cartMutationTail = Promise.resolve();
  let cartMutationSerial = 0;

  let activeContext = null;
  let activeProduct = null;
  let activeVariant = null;
  let selectedOptions = [];
  let lastFocusedElement = null;

  function rootUrl() {
    return window.Shopify?.routes?.root || "/";
  }

  function cartAddUrl() {
    return window.routes?.cart_add_url || `${rootUrl()}cart/add.js`;
  }

  function cartChangeUrl() {
    return window.routes?.cart_change_url || `${rootUrl()}cart/change.js`;
  }

  function cartUpdateUrl() {
    return window.routes?.cart_update_url || `${rootUrl()}cart/update.js`;
  }

  function cartJsonUrl() {
    return `${rootUrl()}cart.js`;
  }

  function sectionUrl() {
    const url = new URL(window.location.href);
    url.search = "";
    url.hash = "";
    url.searchParams.set("section_id", SECTION_ID);
    return url.toString();
  }

  async function fetchCartState() {
    const response = await fetch(cartJsonUrl(), {
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" }
    });

    if (!response.ok) {
      throw new Error(`Cart request failed: ${response.status}`);
    }

    return response.json();
  }

  function propertyHash(properties) {
    const result = {};

    const addEntry = (key, value) => {
      const normalizedKey = String(key ?? "").trim();
      if (!normalizedKey || value === null || value === undefined) return;

      result[normalizedKey] =
        typeof value === "string" ? value : String(value);
    };

    if (Array.isArray(properties)) {
      properties.forEach((entry) => {
        if (Array.isArray(entry)) {
          addEntry(entry[0], entry[1]);
          return;
        }

        if (!entry || typeof entry !== "object") return;

        if ("first" in entry || "last" in entry) {
          addEntry(entry.first, entry.last);
          return;
        }

        if ("name" in entry || "value" in entry) {
          addEntry(entry.name, entry.value);
          return;
        }

        Object.entries(entry).forEach(([key, value]) =>
          addEntry(key, value)
        );
      });

      return result;
    }

    if (properties && typeof properties === "object") {
      Object.entries(properties).forEach(([key, value]) =>
        addEntry(key, value)
      );
    }

    return result;
  }

  function normalizedProperties(properties) {
    const source = propertyHash(properties);

    return JSON.stringify(
      Object.keys(source)
        .sort()
        .reduce((result, key) => {
          result[key] = source[key];
          return result;
        }, {})
    );
  }

  function sellingPlanId(item) {
    return String(
      item?.selling_plan_allocation?.selling_plan?.id ||
        item?.selling_plan_allocation?.selling_plan_id ||
        ""
    );
  }

  function sameLineIdentity(item, variantId, properties, sellingPlan) {
    if (!item) return false;

    return (
      String(item.variant_id) === String(variantId) &&
      normalizedProperties(item.properties) ===
        normalizedProperties(properties) &&
      sellingPlanId(item) === String(sellingPlan || "")
    );
  }

  function findLiveLine(cart, context) {
    const items = Array.isArray(cart?.items) ? cart.items : [];

    return (
      items.find((item) => String(item.key) === String(context.lineKey)) ||
      items.find((item) =>
        sameLineIdentity(
          item,
          context.variantId,
          context.properties,
          context.sellingPlan
        )
      ) ||
      null
    );
  }

  function findVariantLine(cart, variantId, properties, sellingPlan) {
    return (
      (cart?.items || []).find((item) =>
        sameLineIdentity(item, variantId, properties, sellingPlan)
      ) || null
    );
  }

  function isProtectionItem(item) {
    if (!item) return false;

    const searchable = [
      item.product_title,
      item.title,
      item.variant_title,
      item.handle,
      item.vendor,
      ...Object.keys(item.properties || {}),
      ...Object.values(item.properties || {})
    ]
      .filter(Boolean)
      .join(" ");

    return PROTECTION_TEXT.test(searchable);
  }

  function protectionLines(cart, widget = protectionWidgetRoot()) {
    const widgetVariantId = protectionVariantId(widget);
    const variantId = widgetVariantId || knownProtectionVariantId;

    if (widgetVariantId) knownProtectionVariantId = widgetVariantId;

    return (cart?.items || []).filter(
      (item) =>
        isProtectionItem(item) ||
        (variantId && String(item.variant_id) === String(variantId))
    );
  }

  function merchandiseItemCount(cart) {
    return (cart?.items || []).reduce(
      (total, item) =>
        total + (isProtectionItem(item) ? 0 : Number(item.quantity || 0)),
      0
    );
  }

  function merchandiseSubtotal(cart) {
    return (cart?.items || []).reduce(
      (total, item) =>
        total +
        (isProtectionItem(item) ? 0 : Number(item.final_line_price || 0)),
      0
    );
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function money(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "USD"
    }).format(Number(cents || 0) / 100);
  }

  function normalizedImageUrl(value) {
    if (!value) return "";
    const stringValue = String(value).trim();
    return stringValue.startsWith("//")
      ? `${window.location.protocol}${stringValue}`
      : stringValue;
  }

  function variantOptions(variant) {
    if (Array.isArray(variant?.options)) {
      return variant.options.map(String);
    }

    return [variant?.option1, variant?.option2, variant?.option3]
      .filter((value) => value !== null && value !== undefined)
      .map(String);
  }

  function productOptions(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const options = Array.isArray(product?.options) ? product.options : [];

    return options.map((option, index) => {
      const position = Number(option?.position || index + 1);
      const name =
        typeof option === "string"
          ? option
          : option?.name || `Option ${position}`;
      const supplied =
        typeof option === "object" && Array.isArray(option?.values)
          ? option.values
          : [];
      const fromVariants = variants
        .map((variant) => variantOptions(variant)[position - 1])
        .filter(Boolean);

      return {
        name,
        position,
        values: Array.from(new Set([...supplied, ...fromVariants].map(String)))
      };
    });
  }

  function variantImage(variant, product) {
    const media = variant?.featured_media || null;
    const image = media?.preview_image || variant?.featured_image || null;

    const variantSource = normalizedImageUrl(
      typeof image === "string" ? image : image?.src || image?.url
    );

    const productSource = normalizedImageUrl(
      typeof product?.featured_image === "string"
        ? product.featured_image
        : product?.featured_image?.src ||
            product?.featured_image?.url ||
            (typeof product?.images?.[0] === "string"
              ? product.images[0]
              : product?.images?.[0]?.src || product?.images?.[0]?.url)
    );

    return variantSource || productSource;
  }

  function plainDescription(html) {
    if (!html) return "";

    const documentFragment = new DOMParser().parseFromString(
      `<div>${html}</div>`,
      "text/html"
    );

    documentFragment
      .querySelectorAll(
        "script, style, noscript, iframe, form, button, svg, table"
      )
      .forEach((element) => element.remove());

    const firstUsefulBlock = Array.from(
      documentFragment.querySelectorAll("p, li")
    )
      .map((element) => element.textContent.replace(/\s+/g, " ").trim())
      .find((text) => text.length >= 35);

    const text = (firstUsefulBlock || documentFragment.body.textContent || "")
      .replace(/\s+/g, " ")
      .trim();

    if (text.length <= 280) return text;

    const shortened = text.slice(0, 280);
    const sentenceEnd = Math.max(
      shortened.lastIndexOf(". "),
      shortened.lastIndexOf("! "),
      shortened.lastIndexOf("? ")
    );

    return sentenceEnd >= 140
      ? shortened.slice(0, sentenceEnd + 1)
      : `${shortened.replace(/\s+\S*$/, "")}…`;
  }

  function setTriggerLoading(trigger, loading) {
    if (!trigger) return;

    trigger.classList.toggle("is-loading", loading);
    trigger.classList.toggle("btn-loading", loading);
    trigger.setAttribute("aria-busy", loading ? "true" : "false");

    const container = trigger.closest(".ks-mini-rec, .ks-mini-item");
    container?.classList.toggle("is-options-loading", loading);

    if (!loading) {
      trigger.removeAttribute("aria-busy");
    }
  }

  function recommendationStatus(trigger) {
    return trigger
      .closest(".ks-mini-rec")
      ?.querySelector("[data-ks-mini-cart-status]");
  }

  function setRecommendationStatus(trigger, message = "") {
    const status = recommendationStatus(trigger);
    if (!status) return;

    status.textContent = message;
    status.hidden = !message;
  }

  function productJsonUrl(value, handle) {
    try {
      const url = new URL(value || "", window.location.origin);
      if (url.origin !== window.location.origin) return "";

      url.search = "";
      url.hash = "";
      url.pathname = url.pathname.replace(/\.js$/i, "");
      url.pathname = `${url.pathname.replace(/\/$/, "")}.js`;
      return url.toString();
    } catch (_) {
      return handle
        ? `${rootUrl()}products/${encodeURIComponent(handle)}.js`
        : "";
    }
  }

  async function getProduct(handle, productUrl = "") {
    const cacheKey = `${handle || ""}|${productUrl || ""}`;
    if (productCache.has(cacheKey)) return productCache.get(cacheKey);

    const candidates = [
      productJsonUrl(productUrl, handle),
      handle ? `${rootUrl()}products/${encodeURIComponent(handle)}.js` : ""
    ].filter((value, index, all) => value && all.indexOf(value) === index);

    const request = (async () => {
      let lastError = null;

      for (const url of candidates) {
        try {
          const response = await fetch(url, {
            credentials: "same-origin",
            cache: "no-store",
            headers: {
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest"
            }
          });

          if (!response.ok) {
            lastError = new Error(`Product request failed: ${response.status}`);
            continue;
          }

          const product = await response.json();
          if (Array.isArray(product?.variants) && product.variants.length > 0) {
            return product;
          }

          lastError = new Error("Product response did not include variants.");
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error("Product request could not be created.");
    })();

    productCache.set(cacheKey, request);

    try {
      return await request;
    } catch (error) {
      productCache.delete(cacheKey);
      throw error;
    }
  }

  function embeddedProductFromTrigger(trigger) {
    const item = trigger?.closest?.(".ks-mini-item");
    const source = item?.querySelector(EMBEDDED_PRODUCT_SELECTOR);
    if (!source?.textContent.trim()) return null;

    try {
      const product = JSON.parse(source.textContent);
      return Array.isArray(product?.variants) && product.variants.length > 0
        ? product
        : null;
    } catch (error) {
      console.warn(
        "[Kawaii Stop mini cart] Embedded product options were invalid",
        error
      );
      return null;
    }
  }

  function firstAvailableVariant(product) {
    return (
      product.variants?.find((variant) => variant.available) ||
      product.variants?.[0] ||
      null
    );
  }

  function selectedVariantForContext(product, context) {
    if (context.mode === "edit" && context.variantId) {
      const current = product.variants?.find(
        (variant) => String(variant.id) === String(context.variantId)
      );

      if (current) return current;
    }

    return firstAvailableVariant(product);
  }

  function bestVariant(product, optionIndex, requestedValue) {
    const variants = product.variants || [];
    const available = variants.filter((variant) => variant.available);

    const exact = available.find((variant) =>
      variantOptions(variant).every(
        (value, index) => value === selectedOptions[index]
      )
    );

    if (exact) return exact;

    const candidates = available.filter(
      (variant) => variantOptions(variant)[optionIndex] === requestedValue
    );

    candidates.sort((a, b) => {
      const score = (variant) =>
        variantOptions(variant).reduce(
          (total, value, index) =>
            total + (value === selectedOptions[index] ? 1 : 0),
          0
        );

      return score(b) - score(a);
    });

    return candidates[0] || variants[0] || null;
  }

  function optionValueIsAvailable(product, optionIndex, candidateValue) {
    return (product.variants || []).some((variant) => {
      if (!variant.available) return false;

      const options = variantOptions(variant);

      return options.every((value, index) => {
        if (index === optionIndex) return value === candidateValue;
        return !selectedOptions[index] || value === selectedOptions[index];
      });
    });
  }

  function optionMarkup(product) {
    const options = productOptions(product);

    if ((product.variants || []).length <= 1 || options.length === 0) {
      return "";
    }

    return `
      <div class="product-variant-quick-view ks-mini-qv-variant-host">
        <div class="ks-mini-qv-variants" aria-label="Product options">
          ${options
            .map((option, optionIndex) => {
              const isColor = /colou?r/i.test(option.name);

              return `
                <fieldset class="ks-mini-qv-option-group${
                  isColor ? " is-color" : ""
                }">
                  <legend class="ks-mini-qv-option-group__legend">
                    <span>${escapeHtml(option.name)}: </span>
                    <strong data-ks-mini-selected-option="${optionIndex}"></strong>
                  </legend>

                  <div class="ks-mini-qv-option-group__choices">
                    ${option.values
                      .map(
                        (value) => `
                          <button
                            type="button"
                            class="ks-mini-qv-option${
                              isColor ? " is-color" : ""
                            }"
                            data-option-index="${optionIndex}"
                            data-value="${escapeHtml(value)}"
                            aria-pressed="false"
                          >
                            ${
                              isColor
                                ? `<span class="ks-mini-qv-option__swatch" aria-hidden="true" data-color-name="${escapeHtml(
                                    value
                                  )}"></span>`
                                : ""
                            }
                            <span class="ks-mini-qv-option__label">${escapeHtml(
                              value
                            )}</span>
                          </button>
                        `
                      )
                      .join("")}
                  </div>
                </fieldset>
              `;
            })
            .join("")}
        </div>
      </div>
    `;
  }

  function modalMarkup(product, variant, context) {
    const price = Number(variant?.price || 0);
    const compareAt = Number(variant?.compare_at_price || 0);
    const onSale = compareAt > price;
    const description = plainDescription(product.description);
    const image = variantImage(variant, product);
    const productUrl = `${rootUrl()}products/${encodeURIComponent(
      product.handle
    )}`;
    const isEdit = context.mode === "edit";
    const eyebrow = isEdit ? "Edit your item" : "Complete your cart";
    const submitText = isEdit ? "Update item" : "Add to cart";
    const quantity = Math.max(1, Number(context.quantity || 1));

    return `
      <div
        id="${MODAL_ID}"
        class="ks-mini-cart-quickview-dialog"
        data-mode="${isEdit ? "edit" : "add"}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ks-mini-qv-title"
      >
        <div class="dlg-box" role="document">
          <div class="dlg-message">
            <button
              type="button"
              class="dlg-close-x"
              data-ks-mini-qv-close
              aria-label="Close product options"
            >
              <span aria-hidden="true">×</span>
            </button>

            <div class="bls__product-quickview bls__product-single">
              <div class="ks-mini-qv-layout">
                <div class="bls__product-img-quickview">
                  <div class="ks-mini-qv-image-stage">
                    <img
                      class="ks-mini-qv-main-image"
                      src="${escapeHtml(image)}"
                      alt="${escapeHtml(product.title)}"
                      loading="eager"
                    >
                  </div>
                </div>

                <div class="bls__quickview-content-wap">
                  <div class="bls__quickview-content">
                    <div class="ks-mini-qv-sale-wrap" ${onSale ? "" : "hidden"}>
                      <span class="bls__sale-label" data-ks-mini-qv-sale>
                        ${onSale ? `Save ${money(compareAt - price)}` : ""}
                      </span>
                    </div>

                    <p class="ks-mini-qv-eyebrow">${eyebrow}</p>

                    <h3 id="ks-mini-qv-title" class="bls__product-title">
                      ${escapeHtml(product.title)}
                    </h3>

                    <div class="ks-mini-qv-live-price">
                      <span class="ks-mini-qv-live-price__current" data-ks-mini-qv-price>
                        ${money(price)}
                      </span>
                      <s class="ks-mini-qv-live-price__compare" data-ks-mini-qv-compare ${
                        onSale ? "" : "hidden"
                      }>
                        ${onSale ? money(compareAt) : ""}
                      </s>
                    </div>

                    ${
                      description
                        ? `<div class="bls__single-shortdes ks-mini-qv-description">${escapeHtml(
                            description
                          )}</div>`
                        : ""
                    }

                    ${optionMarkup(product)}

                    <p class="ks-mini-qv-availability" data-ks-mini-qv-availability></p>

                    <form class="product-form ks-mini-qv-form">
                      <input type="hidden" name="id" value="${variant.id}">

                      <div class="ks-mini-qv-purchase-row">
                        <div class="quantity" aria-label="Quantity">
                          <button
                            type="button"
                            class="quantity__button"
                            data-ks-mini-qv-minus
                            aria-label="Decrease quantity"
                          >−</button>

                          <input
                            class="quantity__input"
                            type="number"
                            name="quantity"
                            value="${quantity}"
                            min="1"
                            step="1"
                            inputmode="numeric"
                            aria-label="Quantity"
                          >

                          <button
                            type="button"
                            class="quantity__button"
                            data-ks-mini-qv-plus
                            aria-label="Increase quantity"
                          >+</button>
                        </div>

                        <button
                          type="submit"
                          class="product-form__submit button-add-cart-qv"
                        >
                          <span>${submitText}</span>
                        </button>
                      </div>
                    </form>

                    <a class="ks-mini-qv-full-details" href="${escapeHtml(
                      productUrl
                    )}">View full product details</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function setSwatchColors(modal) {
    modal.querySelectorAll(".ks-mini-qv-option.is-color").forEach((button) => {
      const value = button.dataset.value;
      const swatch = button.querySelector(".ks-mini-qv-option__swatch");
      if (!swatch) return;

      if (window.CSS?.supports?.("color", value)) {
        swatch.style.setProperty("--ks-mini-qv-swatch", value);
      }
    });
  }

  function updateModalVariant(modal, variant) {
    if (!variant) return;

    activeVariant = variant;
    selectedOptions = variantOptions(variant);

    const variantInput = modal.querySelector("input[name='id']");
    if (variantInput) variantInput.value = variant.id;

    modal.querySelectorAll(".ks-mini-qv-option").forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const value = button.dataset.value;
      const active = selectedOptions[optionIndex] === value;
      const available = optionValueIsAvailable(
        activeProduct,
        optionIndex,
        value
      );

      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", String(active));
      button.disabled = !available;
    });

    modal
      .querySelectorAll("[data-ks-mini-selected-option]")
      .forEach((label) => {
        const index = Number(label.dataset.ksMiniSelectedOption);
        label.textContent = selectedOptions[index] || "";
      });

    const price = Number(variant.price || 0);
    const compareAt = Number(variant.compare_at_price || 0);
    const onSale = compareAt > price;
    const currentPrice = modal.querySelector("[data-ks-mini-qv-price]");
    const comparePrice = modal.querySelector("[data-ks-mini-qv-compare]");
    const saleWrap = modal.querySelector(".ks-mini-qv-sale-wrap");
    const saleLabel = modal.querySelector("[data-ks-mini-qv-sale]");

    if (currentPrice) currentPrice.textContent = money(price);

    if (comparePrice) {
      comparePrice.hidden = !onSale;
      comparePrice.textContent = onSale ? money(compareAt) : "";
    }

    if (saleWrap) saleWrap.hidden = !onSale;
    if (saleLabel) {
      saleLabel.textContent = onSale ? `Save ${money(compareAt - price)}` : "";
    }

    const image = modal.querySelector(".ks-mini-qv-main-image");
    const newImage = variantImage(variant, activeProduct);

    if (image && newImage && image.src !== newImage) {
      image.classList.add("is-changing");
      image.src = newImage;
      image.alt = `${activeProduct.title} — ${selectedOptions.join(" / ")}`;
      image.onload = () => image.classList.remove("is-changing");
      image.onerror = () => image.classList.remove("is-changing");
    }

    const availability = modal.querySelector(
      "[data-ks-mini-qv-availability]"
    );
    const submit = modal.querySelector("[type='submit']");

    if (availability) {
      availability.classList.toggle("is-unavailable", !variant.available);
      availability.textContent = variant.available
        ? activeContext.mode === "edit"
          ? "Selection available"
          : "In stock and ready to add"
        : "Currently unavailable";
    }

    if (submit) {
      submit.disabled = !variant.available;
      const label = submit.querySelector("span");
      if (label) {
        label.textContent = variant.available
          ? activeContext.mode === "edit"
            ? "Update item"
            : "Add to cart"
          : "Sold out";
      }
    }
  }

  function showModalError(modal, message) {
    modal.querySelector(".ks-mini-qv-error")?.remove();

    const errorBox = document.createElement("div");
    errorBox.className = "ks-mini-qv-error";
    errorBox.setAttribute("role", "alert");
    errorBox.textContent = message;

    modal
      .querySelector(".ks-mini-qv-form")
      ?.insertAdjacentElement("beforebegin", errorBox);
  }

  function closeModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;

    modal.remove();
    document.documentElement.classList.remove("ks-mini-cart-qv-open");
    activeContext = null;
    activeProduct = null;
    activeVariant = null;
    selectedOptions = [];
    lastFocusedElement?.focus?.();
  }

  function protectionWidgetRoot(scope = document) {
    const checkout = scope.querySelector?.(".ks-mini-cart__checkout");
    if (!checkout) return null;

    const directChild = Array.from(checkout.children).find((child) =>
      PROTECTION_TEXT.test(child.textContent || "")
    );

    if (directChild) return directChild;

    const candidate = Array.from(
      checkout.querySelectorAll(PROTECTION_NODE_SELECTOR)
    ).find((element) => {
      const text =
        element.textContent ||
        element.getAttribute("aria-label") ||
        element.getAttribute("title") ||
        "";
      return PROTECTION_TEXT.test(text) ||
        PROTECTION_TEXT.test(element.closest("label")?.textContent || "");
    });

    if (!candidate) return null;

    let root = candidate;
    while (root.parentElement && root.parentElement !== checkout) {
      root = root.parentElement;
    }

    return root.parentElement === checkout ? root : null;
  }

  function detachProtectionWidget(form) {
    const widget = protectionWidgetRoot(form);
    if (!widget) return null;

    widget.remove();
    return widget;
  }

  function restoreProtectionWidget(form, widget) {
    if (!widget) return;

    const checkout = form.querySelector(".ks-mini-cart__checkout");
    if (!checkout) return;

    const serverWidget = protectionWidgetRoot(form);
    if (serverWidget && serverWidget !== widget) serverWidget.remove();

    const viewCart = checkout.querySelector(".ks-mini-cart__view-cart");
    checkout.insertBefore(widget, viewCart || null);
  }

  function protectionToggle(widget = protectionWidgetRoot()) {
    if (!widget) return null;

    return (
      widget.querySelector('input[type="checkbox"]') ||
      widget.querySelector('button[role="switch"]') ||
      widget.querySelector('[aria-checked]') ||
      widget.querySelector('[aria-pressed]') ||
      null
    );
  }

  function protectionToggleState(toggle) {
    if (!toggle) return false;
    if (toggle.matches('input[type="checkbox"]')) return toggle.checked;

    const state =
      toggle.getAttribute("aria-checked") ||
      toggle.getAttribute("aria-pressed");

    if (state !== null) return state === "true";

    return (toggle.className || "")
      .toString()
      .split(/\s+/)
      .some((name) => /(?:active|checked|enabled|selected|on)/i.test(name));
  }

  function setProtectionBusy(widget, busy) {
    if (!widget) return;

    widget.classList.toggle("is-ks-protection-loading", busy);
    widget.setAttribute("aria-busy", busy ? "true" : "false");

    const toggle = protectionToggle(widget);
    if (toggle && "disabled" in toggle) toggle.disabled = busy;

    if (!busy) widget.removeAttribute("aria-busy");
  }

  function positiveInteger(value) {
    const match = String(value ?? "").match(/\b\d{6,}\b/);
    return match ? Number(match[0]) : 0;
  }

  function findVariantIdInObject(
    value,
    seen = new WeakSet(),
    depth = 0,
    budget = { remaining: 400 }
  ) {
    if (!value || typeof value !== "object") return 0;
    if (depth > 5 || budget.remaining <= 0 || seen.has(value)) return 0;

    seen.add(value);
    budget.remaining -= 1;

    let entries = [];
    try {
      entries = Object.entries(value);
    } catch (_) {
      return 0;
    }

    for (const [key, nested] of entries) {
      if (/variant.*id|id.*variant/i.test(key)) {
        const id = positiveInteger(nested);
        if (id) return id;
      }
    }

    for (const [, nested] of entries) {
      if (nested && typeof nested === "object") {
        const id = findVariantIdInObject(
          nested,
          seen,
          depth + 1,
          budget
        );
        if (id) return id;
      }
    }

    return 0;
  }

  function protectionVariantId(widget) {
    if (!widget) return 0;

    const nodes = [widget, ...widget.querySelectorAll("*")];

    for (const node of nodes) {
      for (const [key, value] of Object.entries(node.dataset || {})) {
        if (/variant.*id|id.*variant/i.test(key)) {
          const id = positiveInteger(value);
          if (id) return id;
        }
      }

      for (const attribute of [
        "data-variant-id",
        "data-product-variant-id",
        "data-variant",
        "variant-id"
      ]) {
        const id = positiveInteger(node.getAttribute?.(attribute));
        if (id) return id;
      }

      if (
        node.matches?.('input[type="hidden"][name="id"], select[name="id"]')
      ) {
        const id = positiveInteger(node.value);
        if (id) return id;
      }

      const urlValue =
        node.getAttribute?.("href") || node.getAttribute?.("action") || "";
      const urlVariant = urlValue.match(/[?&]variant=(\d+)/i)?.[1];
      if (urlVariant) return Number(urlVariant);
    }

    for (const script of widget.querySelectorAll(
      'script[type="application/json"], script:not([src])'
    )) {
      const text = script.textContent || "";
      const match = text.match(
        /["']?(?:variant_id|variantId|variant-id)["']?\s*[:=]\s*["']?(\d{6,})/i
      );
      if (match) return Number(match[1]);

      try {
        const id = findVariantIdInObject(JSON.parse(text));
        if (id) return id;
      } catch (_) {
        // Non-JSON inline scripts are covered by the regular expression above.
      }
    }

    for (const globalObject of [
      window.simplyInsurance,
      window.SimplyInsurance,
      window.simplyinsurance,
      window.SSP,
      window.ssp
    ]) {
      const id = findVariantIdInObject(globalObject);
      if (id) return id;
    }

    return knownProtectionVariantId;
  }

  function rememberProtectionVariantId(widget) {
    const id = protectionVariantId(widget);
    if (id) knownProtectionVariantId = id;
    return id || knownProtectionVariantId;
  }

  function syncProtectionToggle(cart) {
    const widget = protectionWidgetRoot();
    const toggle = protectionToggle(widget);
    if (!toggle) return;

    const protectedCart = protectionLines(cart, widget).length > 0;

    if (toggle.matches('input[type="checkbox"]')) {
      toggle.checked = protectedCart;
    } else {
      if (toggle.hasAttribute("aria-checked")) {
        toggle.setAttribute("aria-checked", protectedCart ? "true" : "false");
      }
      if (toggle.hasAttribute("aria-pressed")) {
        toggle.setAttribute("aria-pressed", protectedCart ? "true" : "false");
      }
    }
  }

  function notifyCartUpdated(cart) {
    document.dispatchEvent(
      new CustomEvent("cart:updated", {
        detail: { cart, source: "ks-mini-cart" }
      })
    );
    document.dispatchEvent(
      new CustomEvent("cart:refresh", {
        detail: { cart, source: "ks-mini-cart" }
      })
    );

    if (
      typeof window.publish === "function" &&
      window.PUB_SUB_EVENTS?.cartUpdate
    ) {
      window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: "ks-mini-cart",
        cartData: cart
      });
    }
  }

  function callProtectionReinitializer() {
    for (const candidate of [
      window.simplyInsurance,
      window.SimplyInsurance,
      window.simplyinsurance,
      window.SSP,
      window.ssp
    ]) {
      candidate?.init?.();
      candidate?.initialize?.();
      candidate?.refresh?.();
      candidate?.render?.();
    }
  }

  function delay(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function enqueueCartMutation(label, task) {
    const serial = ++cartMutationSerial;
    const run = cartMutationTail
      .catch(() => undefined)
      .then(async () => {
        document.documentElement.classList.add("ks-mini-cart-mutating");

        try {
          return await task({ label, serial });
        } finally {
          document.documentElement.classList.remove("ks-mini-cart-mutating");
        }
      });

    cartMutationTail = run.catch(() => undefined);
    return run;
  }

  async function reconcileProtectionNow(widget, desiredState) {
    if (!widget || protectionMutationActive) return;

    protectionMutationActive = true;
    setProtectionBusy(widget, true);

    try {
      // Give the installed app first opportunity to complete its own request.
      await new Promise((resolve) => window.setTimeout(resolve, 850));

      let cart = await fetchCartState();
      let liveProtectionLines = protectionLines(cart, widget);
      const cartIsProtected = liveProtectionLines.length > 0;

      if (cartIsProtected !== desiredState) {
        if (desiredState) {
          const variantId = rememberProtectionVariantId(widget);

          if (!variantId) {
            callProtectionReinitializer();
            throw new Error(
              "Kawaii Shield could not identify its protection plan. Refresh the page and try once more."
            );
          }

          await postJson(cartAddUrl(), {
            items: [
              {
                id: Number(variantId),
                quantity: 1
              }
            ]
          });
        } else {
          for (const line of liveProtectionLines) {
            await postJson(cartChangeUrl(), {
              id: line.key,
              quantity: 0
            });
          }
        }
      }

      cart = await fetchCartState();
      liveProtectionLines = protectionLines(cart, widget);

      if (desiredState && liveProtectionLines.length === 0) {
        throw new Error("Kawaii Shield was not added to the cart.");
      }

      if (!desiredState && liveProtectionLines.length > 0) {
        throw new Error("Kawaii Shield could not be removed from the cart.");
      }

      cart = await refreshMiniCart();
      syncProtectionToggle(cart);
      notifyCartUpdated(cart);
    } catch (error) {
      console.error("[Kawaii Stop mini cart] Protection update failed", error);

      const latestCart = await fetchCartState().catch(() => null);
      if (latestCart) syncProtectionToggle(latestCart);

      showDrawerToast(
        error.message || "Kawaii Shield could not be updated. Please try again.",
        true
      );
    } finally {
      protectionMutationActive = false;
      setProtectionBusy(protectionWidgetRoot() || widget, false);
    }
  }

  function reconcileProtection(widget, desiredState) {
    return enqueueCartMutation("protection-toggle", () =>
      reconcileProtectionNow(widget, desiredState)
    );
  }

  function queueProtectionReconcile(target) {
    const widget = protectionWidgetRoot();
    if (!widget || !widget.contains(target)) return;

    window.clearTimeout(protectionReconcileTimer);
    protectionReconcileTimer = window.setTimeout(() => {
      const toggle = protectionToggle(widget);
      reconcileProtection(widget, protectionToggleState(toggle));
    }, 30);
  }

  function updateHeaderCounts(cart) {
    if (!cart || cart.item_count === undefined) return;

    const visibleItemCount = merchandiseItemCount(cart);

    document.querySelectorAll(".cart-count").forEach((count) => {
      count.textContent = count.classList.contains("cart-count-drawer")
        ? `(${visibleItemCount})`
        : visibleItemCount;
    });

    document.querySelectorAll(".quantity-cart").forEach((count) => {
      count.textContent = visibleItemCount;
    });

    document.querySelectorAll(".quantity__label").forEach((label) => {
      label.classList.toggle("hidden", visibleItemCount < 1);
    });

    const headerTotal = document.querySelector("header-total-price");
    headerTotal?.updateTotal?.(cart);
  }

  function cartStateSignature(cart) {
    return JSON.stringify({
      item_count: Number(cart?.item_count || 0),
      total_price: Number(cart?.total_price || 0),
      items: (cart?.items || []).map((item) => ({
        key: String(item.key || ""),
        variant_id: String(item.variant_id || ""),
        quantity: Number(item.quantity || 0),
        final_line_price: Number(item.final_line_price || 0)
      }))
    });
  }

  async function waitForStableCart(initialCart = null) {
    let previous = initialCart || (await fetchCartState());

    for (let attempt = 0; attempt < 4; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, 220));

      const current = await fetchCartState();
      if (cartStateSignature(current) === cartStateSignature(previous)) {
        return current;
      }

      previous = current;
    }

    return previous;
  }

  async function fetchFreshMiniCartSnapshot() {
    let cart = await waitForStableCart();

    for (let attempt = 0; attempt < 2; attempt += 1) {
      const response = await fetch(sectionUrl(), {
        credentials: "same-origin",
        cache: "no-store",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });

      if (!response.ok) {
        throw new Error(`Mini-cart refresh failed: ${response.status}`);
      }

      const sectionText = await response.text();
      const afterSectionCart = await fetchCartState();

      if (
        cartStateSignature(afterSectionCart) === cartStateSignature(cart)
      ) {
        return { cart: afterSectionCart, sectionText };
      }

      cart = await waitForStableCart(afterSectionCart);
    }

    const response = await fetch(sectionUrl(), {
      credentials: "same-origin",
      cache: "no-store",
      headers: { "X-Requested-With": "XMLHttpRequest" }
    });

    if (!response.ok) {
      throw new Error(`Mini-cart refresh failed: ${response.status}`);
    }

    return {
      cart: await fetchCartState(),
      sectionText: await response.text()
    };
  }

  async function refreshMiniCart(renderedSection = "") {
    let cart;
    let sectionText;

    if (renderedSection) {
      cart = await fetchCartState();
      sectionText = renderedSection;
    } else {
      const snapshot = await fetchFreshMiniCartSnapshot();
      cart = snapshot.cart;
      sectionText = snapshot.sectionText;
    }
    const parsed = new DOMParser().parseFromString(sectionText, "text/html");
    const nextForm = parsed.querySelector(`#${SECTION_ID}`);
    const currentForm = document.getElementById(SECTION_ID);

    if (!nextForm || !currentForm) {
      throw new Error("Updated mini-cart markup was not found.");
    }

    const preservedProtectionWidget = detachProtectionWidget(currentForm);

    currentForm.innerHTML = nextForm.innerHTML;
    restoreProtectionWidget(currentForm, preservedProtectionWidget);
    updateHeaderCounts(cart);

    const miniCart = document.querySelector("cart-notification");
    miniCart?.cartAction?.();

    const freeShipping = document.querySelector(
      ".ks-mini-cart free-ship-progress-bar"
    );
    freeShipping?.init?.(merchandiseSubtotal(cart));

    window.Shopify?.termsConditionsAction?.();
    window.BlsLazyloadImg?.init?.();
    window.BlsSettingsSwiper?.init?.();
    callProtectionReinitializer();
    syncProtectionToggle(cart);
    notifyCartUpdated(cart);

    return cart;
  }

  async function postJson(url, payload) {
    const response = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify(payload)
    });

    const responseText = await response.text();
    let result = {};

    try {
      result = responseText ? JSON.parse(responseText) : {};
    } catch (_) {
      result = {};
    }

    if (!response.ok || result.status || result.errors) {
      const providerMessage =
        typeof result.errors === "string"
          ? result.errors
          : result.description || result.message;
      const error = new Error(
        providerMessage ||
          `The cart could not be updated (${response.status}).`
      );

      error.httpStatus = response.status;
      error.cartPayload = result;
      throw error;
    }

    return result;
  }

  function isStaleLineError(error) {
    const message = String(error?.message || "").toLowerCase();
    return (
      Number(error?.httpStatus) === 400 ||
      message.includes("no valid id") ||
      message.includes("no valid line") ||
      message.includes("line item") && message.includes("not found")
    );
  }

  async function addRecommendation(quantity) {
    return postJson(cartAddUrl(), {
      id: Number(activeVariant.id),
      quantity,
      sections: [SECTION_ID],
      sections_url: window.location.pathname
    });
  }

  function cartLineAddItem(line, quantity = Number(line?.quantity || 1)) {
    const item = {
      id: Number(line.variant_id),
      quantity: Math.max(1, Number(quantity || 1))
    };

    const properties = propertyHash(line.properties);
    const plan = sellingPlanId(line);

    if (Object.keys(properties).length > 0) {
      item.properties = properties;
    }

    if (plan) {
      item.selling_plan = Number(plan);
    }

    return item;
  }

  async function ensureProtectionSurvives(beforeCart) {
    const protectedBefore = protectionLines(beforeCart);
    if (protectedBefore.length === 0) return false;

    // Most edits now run as one atomic update request, so Shield should remain.
    // If the app removes it while recalculating its price tier, allow the app
    // enough time to restore the correct tier before using our fallback.
    await new Promise((resolve) => window.setTimeout(resolve, 220));

    let currentCart = await fetchCartState();
    if (protectionLines(currentCart).length > 0) return false;

    callProtectionReinitializer();
    await new Promise((resolve) => window.setTimeout(resolve, 850));

    currentCart = await fetchCartState();
    if (protectionLines(currentCart).length > 0) return false;

    const widgetVariantId = rememberProtectionVariantId(
      protectionWidgetRoot()
    );
    const fallbackItems = protectedBefore.map((line, index) => {
      const item = cartLineAddItem(line);

      if (index === 0 && widgetVariantId) {
        item.id = Number(widgetVariantId);
      }

      return item;
    });

    await postJson(cartAddUrl(), { items: fallbackItems });

    const restoredCart = await fetchCartState();
    if (protectionLines(restoredCart).length === 0) {
      throw new Error("Kawaii Shield could not be preserved during the item update.");
    }

    // The mutation response's bundled section was rendered before this
    // restoration. Tell the caller to fetch fresh mini-cart markup.
    return true;
  }

  function cartItemContext(item) {
    if (!item) return null;

    const edit = item.querySelector(EDIT_TRIGGER);

    return {
      mode: "line",
      handle:
        item.dataset.productHandle ||
        item.getAttribute("product-handle") ||
        edit?.dataset.productHandle ||
        "",
      lineKey:
        item.dataset.ksLineKey ||
        edit?.dataset.lineKey ||
        item.querySelector("[data-index]")?.dataset.index ||
        "",
      variantId:
        item.dataset.ksVariantId ||
        edit?.dataset.currentVariantId ||
        "",
      quantity: Number(
        item.dataset.ksQuantity ||
          item.querySelector(".quantity__input")?.value ||
          edit?.dataset.quantity ||
          1
      ),
      sellingPlan:
        item.dataset.ksSellingPlan || edit?.dataset.sellingPlan || "",
      properties: parseProperties(item)
    };
  }

  function setCartItemBusy(item, busy) {
    if (!item) return;

    item.classList.toggle("loading", busy);
    item.setAttribute("aria-busy", busy ? "true" : "false");

    item
      .querySelectorAll("button, input, a[role='button']")
      .forEach((control) => {
        if ("disabled" in control) control.disabled = busy;
      });

    if (!busy) item.removeAttribute("aria-busy");
  }

  async function changeResolvedLine(context, quantityOrResolver, options = {}) {
    const attempts = Number(options.attempts || 5);
    let lastError = null;

    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const cart = await fetchCartState();
      const line = findLiveLine(cart, context);

      if (!line) {
        const targetWhenMissing =
          typeof quantityOrResolver === "function"
            ? 0
            : Number(quantityOrResolver || 0);

        if (targetWhenMissing === 0) return cart;

        throw new Error(
          "This cart item changed before the update completed. Please try once more."
        );
      }

      const currentQuantity = Number(line.quantity || 0);
      const desiredQuantity = Math.max(
        0,
        Math.trunc(
          typeof quantityOrResolver === "function"
            ? quantityOrResolver(currentQuantity, line, cart)
            : Number(quantityOrResolver || 0)
        )
      );

      if (desiredQuantity === currentQuantity) return cart;

      try {
        await postJson(cartChangeUrl(), {
          id: String(line.key),
          quantity: desiredQuantity
        });
      } catch (error) {
        lastError = error;

        if (isStaleLineError(error) && attempt < attempts - 1) {
          await delay(90 + attempt * 90);
          continue;
        }

        throw error;
      }

      const verifiedCart = await fetchCartState();
      const verifiedLine = findLiveLine(verifiedCart, {
        ...context,
        lineKey: ""
      });

      const verified =
        desiredQuantity === 0
          ? !verifiedLine
          : Number(verifiedLine?.quantity || 0) === desiredQuantity;

      if (verified) return verifiedCart;

      lastError = new Error(
        desiredQuantity === 0
          ? "The item could not be removed from the cart."
          : "The requested quantity was not saved."
      );
      await delay(100 + attempt * 100);
    }

    throw lastError || new Error("The cart could not be updated.");
  }

  async function mutateDrawerLine(item, quantityOrResolver, successMessage) {
    const context = cartItemContext(item);
    if (!context) return;

    setCartItemBusy(item, true);

    try {
      await enqueueCartMutation("line-change", async () => {
        const beforeCart = await fetchCartState();
        await changeResolvedLine(context, quantityOrResolver);
        await ensureProtectionSurvives(beforeCart);
        await refreshMiniCart();
      });

      if (successMessage) showDrawerToast(successMessage);
    } catch (error) {
      console.error("[Kawaii Stop mini cart] Line update failed", error);
      showDrawerToast(
        error.message || "The cart could not be updated. Please try again.",
        true
      );
      setCartItemBusy(item, false);
    }
  }

  function wishlistItems() {
    try {
      const value = JSON.parse(
        localStorage.getItem("bls__wishlist-items") || "[]"
      );
      return Array.isArray(value) ? value : [];
    } catch (_) {
      return [];
    }
  }

  function openWishlistPrompt(item) {
    const prompt = item?.querySelector(".ks-mini-item__wishlist");
    if (!prompt) return false;
    prompt.style.display = "flex";
    return true;
  }

  function closeWishlistPrompt(item) {
    const prompt = item?.querySelector(".ks-mini-item__wishlist");
    if (prompt) prompt.style.display = "none";
  }

  function addItemToWishlist(button) {
    if (!button) return;

    if (window.BlsSubActionProduct?.handleWishlistFunctionClick) {
      window.BlsSubActionProduct.handleWishlistFunctionClick({
        preventDefault() {},
        currentTarget: button
      });
      return;
    }

    const handle = button.dataset.productHandle;
    if (!handle) return;
    const items = wishlistItems();
    if (!items.includes(handle)) items.push(handle);
    localStorage.setItem("bls__wishlist-items", JSON.stringify(items));
  }

  function handleDrawerCartControl(event) {
    const item = event.target.closest?.(".ks-mini-item");
    if (!item || !item.closest(".ks-mini-cart")) return false;

    const wishlistClose = event.target.closest(".cart-close-wishlist");
    if (wishlistClose) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closeWishlistPrompt(item);
      return true;
    }

    const wishlistYes = event.target.closest(".btn-minicart-add-wishlist-js");
    if (wishlistYes) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      addItemToWishlist(wishlistYes);
      closeWishlistPrompt(item);
      mutateDrawerLine(item, 0, "Item moved to your wishlist");
      return true;
    }

    const wishlistNo = event.target.closest(".btn-minicart-remove-js");
    if (wishlistNo) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      closeWishlistPrompt(item);
      mutateDrawerLine(item, 0, "Item removed");
      return true;
    }

    const remove = event.target.closest(
      ".ks-mini-item__remove, .cart-remove"
    );
    if (remove) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const handle =
        item.dataset.productHandle || item.getAttribute("product-handle") || "";
      const alreadyWishlisted = wishlistItems().includes(handle);

      if (!alreadyWishlisted && openWishlistPrompt(item)) return true;

      mutateDrawerLine(item, 0, "Item removed");
      return true;
    }

    const quantityButton = event.target.closest(
      ".ks-mini-item__quantity .quantity__button"
    );
    if (quantityButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const delta = quantityButton.name === "minus" ? -1 : 1;
      mutateDrawerLine(
        item,
        (currentQuantity) => currentQuantity + delta,
        "Cart quantity updated"
      );
      return true;
    }

    return false;
  }

  function handleDrawerQuantityChange(event) {
    const input = event.target.closest?.(
      ".ks-mini-item .quantity__input[name='updates[]']"
    );
    if (!input || !input.closest(".ks-mini-cart")) return false;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const item = input.closest(".ks-mini-item");
    const desired = Math.max(0, Math.trunc(Number(input.value || 0)));
    mutateDrawerLine(item, desired, "Cart quantity updated");
    return true;
  }

  async function updateExistingItem(quantity) {
    const beforeCart = await fetchCartState();
    const originalLine = findLiveLine(beforeCart, activeContext);

    if (!originalLine) {
      throw new Error(
        "This cart item changed while the options window was open. Close it and try again."
      );
    }

    const originalContext = {
      ...activeContext,
      lineKey: originalLine.key,
      variantId: originalLine.variant_id,
      properties: propertyHash(originalLine.properties),
      sellingPlan: sellingPlanId(originalLine)
    };

    if (String(activeVariant.id) === String(originalLine.variant_id)) {
      await changeResolvedLine(originalContext, quantity);
      await ensureProtectionSurvives(beforeCart);
      return {};
    }

    const liveProperties = propertyHash(originalLine.properties);
    const liveSellingPlan = sellingPlanId(originalLine);
    const originalQuantity = Math.max(1, Number(originalLine.quantity || 1));
    const replacementBefore = findVariantLine(
      beforeCart,
      activeVariant.id,
      liveProperties,
      liveSellingPlan
    );
    const replacementQuantityBefore = Number(
      replacementBefore?.quantity || 0
    );
    const replacementPayload = {
      id: Number(activeVariant.id),
      quantity
    };

    if (Object.keys(liveProperties).length > 0) {
      replacementPayload.properties = liveProperties;
    }

    if (liveSellingPlan) {
      replacementPayload.selling_plan = Number(liveSellingPlan);
    }

    const replacementContext = {
      lineKey: replacementBefore?.key || "",
      variantId: activeVariant.id,
      properties: liveProperties,
      sellingPlan: liveSellingPlan
    };

    let replacementAdded = false;
    let originalRemoved = false;

    try {
      // Add first so the protection app never sees a temporary cart without
      // the merchandise line. The original is resolved again afterward, using
      // its live key after all discount/protection recalculation.
      await postJson(cartAddUrl(), { items: [replacementPayload] });
      replacementAdded = true;

      const afterAdd = await fetchCartState();
      const addedReplacement = findVariantLine(
        afterAdd,
        activeVariant.id,
        liveProperties,
        liveSellingPlan
      );

      if (
        !addedReplacement ||
        Number(addedReplacement.quantity || 0) <
          replacementQuantityBefore + quantity
      ) {
        throw new Error("The replacement option was not added correctly.");
      }

      await changeResolvedLine(originalContext, 0);
      originalRemoved = true;

      const verifiedCart = await fetchCartState();
      const originalStillPresent = findLiveLine(verifiedCart, {
        ...originalContext,
        lineKey: ""
      });
      const verifiedReplacement = findVariantLine(
        verifiedCart,
        activeVariant.id,
        liveProperties,
        liveSellingPlan
      );

      if (originalStillPresent) {
        throw new Error("The original cart option could not be removed.");
      }

      if (
        !verifiedReplacement ||
        Number(verifiedReplacement.quantity || 0) <
          replacementQuantityBefore + quantity
      ) {
        throw new Error("The replacement option was not saved correctly.");
      }

      await ensureProtectionSurvives(beforeCart);
      return {};
    } catch (error) {
      try {
        if (replacementAdded) {
          await changeResolvedLine(
            replacementContext,
            replacementQuantityBefore
          );
        }

        if (originalRemoved) {
          const rollbackCart = await fetchCartState();
          const restoredOriginal = findVariantLine(
            rollbackCart,
            originalLine.variant_id,
            liveProperties,
            liveSellingPlan
          );

          if (!restoredOriginal) {
            await postJson(cartAddUrl(), {
              items: [cartLineAddItem(originalLine, originalQuantity)]
            });
          }
        }

        await ensureProtectionSurvives(beforeCart);
      } catch (rollbackError) {
        console.error(
          "[Kawaii Stop mini cart] Variant replacement rollback failed",
          rollbackError
        );
      }

      throw error;
    }
  }

  function showDrawerToast(message, isError = false) {
    document.querySelector(".ks-mini-cart-toast")?.remove();

    const toast = document.createElement("div");
    toast.className = `ks-mini-cart-toast${isError ? " is-error" : ""}`;
    toast.setAttribute("role", "status");
    toast.textContent = message;

    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add("is-visible"));

    window.setTimeout(() => {
      toast.classList.remove("is-visible");
      window.setTimeout(() => toast.remove(), 220);
    }, 2400);
  }

  async function submitModal(modal) {
    if (!activeVariant?.available) return;

    const form = modal.querySelector(".ks-mini-qv-form");
    const submit = form?.querySelector("[type='submit']");
    const quantityInput = form?.querySelector("[name='quantity']");
    const quantity = Math.max(1, Number(quantityInput?.value || 1));

    if (!form || !submit) return;

    modal.querySelector(".ks-mini-qv-error")?.remove();
    submit.disabled = true;
    submit.classList.add("btn-loading");

    const submitLabel = submit.querySelector("span");
    if (submitLabel) {
      submitLabel.textContent =
        activeContext.mode === "edit" ? "Updating…" : "Adding…";
    }

    try {
      const completedMode = activeContext.mode;

      await enqueueCartMutation(
        completedMode === "edit" ? "variant-edit" : "recommendation-add",
        async () => {
          if (completedMode === "edit") {
            await updateExistingItem(quantity);
          } else {
            await addRecommendation(quantity);
          }

          // Always render from a settled, freshly fetched cart. The response
          // section can contain keys from before Shield/discount repricing.
          await refreshMiniCart();
        }
      );

      closeModal();
      showDrawerToast(
        completedMode === "edit" ? "Cart item updated" : "Added to cart"
      );
    } catch (error) {
      console.error("[Kawaii Stop mini cart] Cart action failed", error);
      showModalError(
        modal,
        error.message || "The cart could not be updated. Please try again."
      );

      submit.disabled = !activeVariant?.available;
      submit.classList.remove("btn-loading");

      if (submitLabel) {
        submitLabel.textContent =
          activeContext.mode === "edit" ? "Update item" : "Add to cart";
      }
    }
  }

  function bindModal(modal) {
    setSwatchColors(modal);
    updateModalVariant(modal, activeVariant);

    modal.addEventListener("click", (event) => {
      if (
        event.target === modal ||
        event.target.closest("[data-ks-mini-qv-close]")
      ) {
        closeModal();
        return;
      }

      const option = event.target.closest(".ks-mini-qv-option");
      if (option && !option.disabled) {
        const optionIndex = Number(option.dataset.optionIndex);
        const requestedValue = option.dataset.value;
        selectedOptions[optionIndex] = requestedValue;

        updateModalVariant(
          modal,
          bestVariant(activeProduct, optionIndex, requestedValue)
        );
        return;
      }

      const quantityInput = modal.querySelector(".quantity__input");

      if (event.target.closest("[data-ks-mini-qv-minus]")) {
        quantityInput.value = Math.max(
          1,
          Number(quantityInput.value || 1) - 1
        );
      }

      if (event.target.closest("[data-ks-mini-qv-plus]")) {
        quantityInput.value = Math.max(
          1,
          Number(quantityInput.value || 1) + 1
        );
      }
    });

    modal.querySelector(".ks-mini-qv-form")?.addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        submitModal(modal);
      }
    );
  }

  function parseProperties(item) {
    const source = item?.querySelector(
      "[data-ks-mini-cart-line-properties]"
    );

    if (!source?.textContent.trim()) return {};

    try {
      return propertyHash(JSON.parse(source.textContent));
    } catch (error) {
      console.warn("[Kawaii Stop mini cart] Line properties were invalid", error);
      return {};
    }
  }

  function contextFromTrigger(trigger) {
    if (trigger.matches(EDIT_TRIGGER)) {
      const item = trigger.closest(".ks-mini-item");

      return {
        mode: "edit",
        handle: trigger.dataset.productHandle,
        productUrl: trigger.dataset.productUrl || item?.dataset.productUrl || "",
        lineKey: trigger.dataset.lineKey,
        variantId: trigger.dataset.currentVariantId,
        quantity: Number(trigger.dataset.quantity || 1),
        sellingPlan: trigger.dataset.sellingPlan || "",
        properties: parseProperties(item)
      };
    }

    return {
      mode: "add",
      handle: trigger.dataset.productHandle,
      productUrl: trigger.dataset.productUrl || "",
      quantity: 1,
      properties: {}
    };
  }

  async function openModal(trigger) {
    const context = contextFromTrigger(trigger);
    if (!context.handle || trigger.classList.contains("is-loading")) return;

    setRecommendationStatus(trigger, "");
    setTriggerLoading(trigger, true);
    lastFocusedElement = trigger;

    try {
      const embeddedProduct =
        context.mode === "edit" ? embeddedProductFromTrigger(trigger) : null;
      const product =
        embeddedProduct ||
        (await getProduct(context.handle, context.productUrl));
      const variant = selectedVariantForContext(product, context);

      if (!variant) {
        throw new Error("No purchasable variant was found.");
      }

      closeModal();
      activeContext = context;
      activeProduct = product;
      activeVariant = variant;
      selectedOptions = variantOptions(variant);

      document.body.insertAdjacentHTML(
        "beforeend",
        modalMarkup(product, variant, context)
      );
      document.documentElement.classList.add("ks-mini-cart-qv-open");

      const modal = document.getElementById(MODAL_ID);
      bindModal(modal);
      modal.querySelector("[data-ks-mini-qv-close]")?.focus();
    } catch (error) {
      console.error("[Kawaii Stop mini cart] Options could not open", error);

      if (trigger.matches(RECOMMENDATION_TRIGGER)) {
        setRecommendationStatus(
          trigger,
          "Options could not load. Please try again."
        );
      } else {
        showDrawerToast("Options could not load. Please try again.", true);
      }
    } finally {
      setTriggerLoading(trigger, false);
    }
  }

  async function initializeMiniCartState() {
    try {
      const cart = await fetchCartState();
      rememberProtectionVariantId(protectionWidgetRoot());
      updateHeaderCounts(cart);

      const freeShipping = document.querySelector(
        ".ks-mini-cart free-ship-progress-bar"
      );
      freeShipping?.init?.(merchandiseSubtotal(cart));

      syncProtectionToggle(cart);
    } catch (error) {
      console.warn(
        "[Kawaii Stop mini cart] Initial cart synchronization failed",
        error
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeMiniCartState, {
      once: true
    });
  } else {
    initializeMiniCartState();
  }

  document.addEventListener(
    "click",
    (event) => {
      if (handleDrawerCartControl(event)) return;

      const protectionRemove = event.target.closest(
        "[data-ks-mini-protection-remove]"
      );

      if (protectionRemove?.closest(".ks-mini-cart")) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const widget = protectionWidgetRoot();
        const toggle = protectionToggle(widget);

        if (toggle?.matches('input[type="checkbox"]')) {
          toggle.checked = false;
        } else if (toggle) {
          if (toggle.hasAttribute("aria-checked")) {
            toggle.setAttribute("aria-checked", "false");
          }
          if (toggle.hasAttribute("aria-pressed")) {
            toggle.setAttribute("aria-pressed", "false");
          }
        }

        reconcileProtection(widget, false);
        return;
      }

      const trigger = event.target.closest(
        `${RECOMMENDATION_TRIGGER}, ${EDIT_TRIGGER}`
      );

      if (!trigger || !trigger.closest(".ks-mini-cart")) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      openModal(trigger);
    },
    true
  );

  document.addEventListener(
    "change",
    (event) => {
      handleDrawerQuantityChange(event);
    },
    true
  );

  document.addEventListener("change", (event) => {
    const toggle = event.target.closest?.(
      'input[type="checkbox"], [role="switch"], [aria-checked], [aria-pressed]'
    );
    if (!toggle || !toggle.closest(".ks-mini-cart")) return;
    queueProtectionReconcile(toggle);
  });

  document.addEventListener("click", (event) => {
    const switchButton = event.target.closest?.(
      'button[role="switch"], button[aria-checked], button[aria-pressed]'
    );
    if (!switchButton || !switchButton.closest(".ks-mini-cart")) return;
    queueProtectionReconcile(switchButton);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && document.getElementById(MODAL_ID)) {
      closeModal();
    }
  });
})();
